import { useState, useCallback } from "react";

export interface InternetFormData {
  // Basic fields
  kontrolEdgePortId: number | null;
  mbps: number | null;
  vlan: number | null;
  automaticVlan: boolean | null;
  redundancy: boolean | null;
  countryCode: string;
  routing: string;
  terms: number | null;
  autoRenew: boolean | null;
  subscriptionId: string;
  name: string;
  tag: string[];
  
  // P2P Addressing
  p2pAddressing: {
    mask: string | number | null;
    peerIp: string;
    gateway: string;
    network: string;
  };
  // Latency Targets - nested structure: { hostname: { interface: [ip1, ip2, ...] } }
  latencyTargets: Record<string, Record<string, string[]>> | null;
  
  // Status toggle (0 = Draft, 1 = Active)
  isDraft: number;
  
  // Internal IDs
  id: number | null;
}

const initialFormData: InternetFormData = {
  kontrolEdgePortId: null,
  mbps: null,
  vlan: null,
  automaticVlan: null,
  redundancy: null,
  countryCode: "",
  routing: "",
  terms: null,
  autoRenew: null,
  subscriptionId: "",
  name: "",
  tag: [],
  p2pAddressing: {
    mask: null,
    peerIp: "",
    gateway: "",
    network: "",
  },
  latencyTargets: null,
  isDraft: 0,
  id: null,
};

export interface UseInternetFormReturn {
  formData: InternetFormData;
  updateField: <K extends keyof InternetFormData>(field: K, value: InternetFormData[K]) => void;
  updateFields: (fields: Partial<InternetFormData>) => void;
  resetForm: () => void;
  setFormData: (data: Partial<InternetFormData>) => void;
  updateP2PAddressing: (field: keyof InternetFormData["p2pAddressing"], value: string | number | null) => void;
}

export const useInternetForm = (initialData?: Partial<InternetFormData>): UseInternetFormReturn => {
  const [formData, setFormDataState] = useState<InternetFormData>({
    ...initialFormData,
    ...initialData,
  });

  const updateField = useCallback(<K extends keyof InternetFormData>(
    field: K,
    value: InternetFormData[K]
  ) => {
    setFormDataState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateFields = useCallback((fields: Partial<InternetFormData>) => {
    setFormDataState((prev) => ({
      ...prev,
      ...fields,
    }));
  }, []);

  const updateP2PAddressing = useCallback((
    field: keyof InternetFormData["p2pAddressing"],
    value: string | number | null
  ) => {
    setFormDataState((prev) => ({
      ...prev,
      p2pAddressing: {
        ...prev.p2pAddressing,
        [field]: value,
      },
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormDataState({
      ...initialFormData,
      ...initialData,
    });
  }, [initialData]);

  const setFormData = useCallback((data: Partial<InternetFormData>) => {
    setFormDataState((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  return {
    formData,
    updateField,
    updateFields,
    resetForm,
    setFormData,
    updateP2PAddressing,
  };
};

